import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

const found = document.getElementById('root');
if (found === null) throw new Error('missing #root');
const el: HTMLElement = found;

/**
 * MSAL v5 redirect-bridge hook (DECISIONS #12): when Entra lands an auth
 * response on the app origin (#code=… / #error=…), this page's ONLY job is
 * to hand the payload back to MSAL. Popup sign-ins broadcast it to the
 * waiting opener and self-close; redirect sign-ins park it in sessionStorage
 * and navigate back to the app URL, where handleRedirectPromise picks it up
 * (store.ts completeRedirectSignIn). Booting the full app here instead is
 * the block_nested_popups failure the first real Entra login hit.
 */
const AUTH_RESPONSE = /[#&?](code|error|ear_jwe)=/;

function renderBridgeNotice(root: HTMLElement): void {
  const div = document.createElement('div');
  div.className = 'empty-state';
  div.style.paddingTop = '18vh';
  div.dataset['testid'] = 'msal-bridge-notice';
  const h = document.createElement('h1');
  h.textContent = 'Sign-in complete';
  const p = document.createElement('p');
  p.textContent = "This window should close itself — if it doesn't, you can close it now.";
  div.append(h, p);
  root.replaceChildren(div);
}

async function boot(): Promise<void> {
  if (AUTH_RESPONSE.test(window.location.hash) || AUTH_RESPONSE.test(window.location.search)) {
    const inPopup = window.opener !== null || window.name.startsWith('msal.');
    try {
      const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
      if (inPopup) {
        // Popup path: the bridge posts the response to the opener and calls
        // window.close() — no navigation, so awaiting is safe. If the browser
        // refused to close us, show a calm notice; never boot a second app
        // copy (that was the block_nested_popups failure, DECISIONS #12).
        await broadcastResponseToMainFrame();
        renderBridgeNotice(el);
        return;
      }
      // Redirect path: fire the bridge but DON'T await it. Its first synchronous
      // pass parks the auth response in sessionStorage and rewrites the URL to
      // the clean home route (…/#/) — everything we need. It then calls
      // NavigationClient, which does location.replace(homeUrl) and returns a
      // promise that only ever REJECTS, after a 30 s timeout. With hash routing
      // that replace is fragment-only (…/#code=… → …/#/): a same-document
      // navigation that never reloads, so awaiting would hang for 30 s and
      // leave a blank page. Instead we fall through and mount the app now, on
      // the already-cleaned URL — initAuth → completeRedirectSignIn reads the
      // parked payload straight from sessionStorage (hasPendingMsalRedirect).
      // If the replace WAS a real reload (query-mode response), this frame is
      // torn down and the fresh load handles it — the render below is a no-op.
      void broadcastResponseToMainFrame().catch(() => {
        // Expected: the fragment-only navigation's 30 s timeout rejection, or a
        // parse error. Either way the app is already mounting below.
      });
    } catch {
      // Not a real MSAL response (state unparseable) — the bridge import failed
      // or the params were stray; boot the app normally.
    }
  }
  const { App } = await import('./App.js');
  createRoot(el).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void boot();
